import { ClickAwayListener, Modal } from '@mui/material'
import { lazy, Suspense, useMemo } from 'preact/compat'
import { route } from 'preact-router'
import Link from '~/components/Link'

import paths from '~/enums/paths'
import { getEntry } from '~/content/collections'

// Route-driven overlay rendered on top of the still-mounted /work grid whenever the URL
// is /work/:slug. The panel spans the full width of the grid parent (page padding only),
// the backdrop is near-black, and the grid texture is reserved for the padded header.
export default function ProjectModal({ slug }: { slug: string }) {
	const entry = getEntry('projects', slug)

	const Body = useMemo(
		() => (entry ? lazy(() => entry.load()) : null),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[slug],
	)

	const close = () => route(paths.work)

	if (!entry || !Body) {
		// Unknown slug: fall back to the landing grid.
		route(paths.work, true)
		return null
	}

	const { meta } = entry

	return (
		<Modal
			open
			// Backdrop clicks are handled by ClickAwayListener below; here we only react to Esc
			// so navigating back to /work isn't triggered twice.
			onClose={(_event, reason) => {
				if (reason === 'escapeKeyDown') close()
			}}
			slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.92)' } } }}
		>
			{/* px-5 matches the page's content padding so the panel lines up with the grid width */}
			<div className="absolute inset-0 flex items-start justify-center overflow-y-auto px-5 py-5">
				<ClickAwayListener onClickAway={close}>
					<article className="relative my-auto w-full bg-ink outline-none">
						<Link
							href={paths.work}
							aria-label="Close project"
							className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center text-2xl leading-none text-white/70 no-underline hover:text-white"
						>
							×
						</Link>

						{/* Header / frontmatter — carries the grid texture, generously padded */}
						<header className="bg-grid-dark px-6 py-10 md:px-12 md:py-14">
							<h1 className="m-0 text-3xl font-black tracking-tight text-white md:text-5xl">
								{meta.name}
							</h1>
							<p className="mt-3 max-w-2xl text-white/70 md:text-lg">{meta.excerpt}</p>
							<div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
								{meta.tags?.map((tag) => (
									<span
										key={tag}
										className="tracking-[0.2em] text-white/[0.45] [font-family:monospace]"
									>
										#{tag}
									</span>
								))}
								{meta.repo && (
									<a
										href={meta.repo}
										target="_blank"
										rel="noreferrer noopener"
										className="tracking-wide text-primary-light no-underline hover:underline"
									>
										Source ↗
									</a>
								)}
								{meta.demo && (
									<a
										href={meta.demo}
										target="_blank"
										rel="noreferrer noopener"
										className="tracking-wide text-primary-light no-underline hover:underline"
									>
										Live demo ↗
									</a>
								)}
							</div>
						</header>

						{/* Body */}
						<div className="px-6 py-10 leading-relaxed text-white/[0.875] md:px-12 md:py-14">
							<Suspense fallback={<p className="opacity-50">Loading…</p>}>
								<Body />
							</Suspense>
						</div>
					</article>
				</ClickAwayListener>
			</div>
		</Modal>
	)
}
